/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'
import { FullModalLayout } from '@components/molecules'
import {
	useAppSelector,
	useAppDispatch,
	shipmentActions,
	routeActions,
	ApiGetDataStatus,
} from '@redux/index'
import { DispatchDetailType, ShipmentLabelFileType, ShipmentType } from '@schemas'
import { mono, boFont, boShared } from '@components/systems/backoffice/backoffice.tokens'
import { printLabel } from '@utils'
import { detailPath } from './DispatchDetail'
import dayjs from 'dayjs'

const EMPTY = '—'

const datetime = (value: string): string =>
	value !== '' ? dayjs(value).format('YYYY-MM-DD HH:mm') : EMPTY

/**
 * 라벨 보기 (`/main/shipping/:dispatchId/label[/:shipmentId]`) — 전체 화면 한 장.
 *
 * 만들기와 **따로 둔다**: 발급은 한 번이지만 보기는 몇 번이고 다시 연다(재인쇄·송장 확인).
 * 주소가 있어 링크로 넘길 수 있다. 라벨 본문은 서버가 S3 에서 읽어 준다(SHIPPING_PLAN §2.2.2).
 */
export const ShipmentLabelView = () => {
	const reduxDispatch = useAppDispatch()

	const dispatchId: string = useAppSelector(state => state.shipment.detailDispatchId)
	const shipmentId: string = useAppSelector(state => state.shipment.labelShipmentId)
	const detail: DispatchDetailType | null = useAppSelector(state => state.shipment.detail)
	const labelFile: ShipmentLabelFileType | null = useAppSelector(
		state => state.shipment.labelFile,
	)
	const labelFileStatus: ApiGetDataStatus = useAppSelector(
		state => state.shipment.labelFileStatus,
	)

	const shipments: ShipmentType[] = Array.isArray(detail?.shipments)
		? (detail?.shipments as ShipmentType[])
		: []
	/** 주소에 배송 건이 없으면 활성 건을 본다 */
	const target: ShipmentType | undefined =
		shipmentId !== ''
			? shipments.find(s => s.id === shipmentId)
			: shipments.find(s => s.canceledAt === '')

	// 볼 배송 건이 정해지면 라벨을 읽는다
	React.useEffect(() => {
		if (target === undefined) return
		if (labelFile?.id === target.id) return
		reduxDispatch(shipmentActions.FETCH_LABEL_FILE(target.id))
	}, [target?.id, labelFile?.id, reduxDispatch])

	/** 미리보기 — base64 를 Blob 으로 바꿔 브라우저 PDF 뷰어에 맡긴다 */
	const previewUrl: string = React.useMemo<string>(() => {
		if (labelFile === null || labelFile.base64 === '') return ''
		try {
			const bytes = Uint8Array.from(atob(labelFile.base64), c => c.charCodeAt(0))
			const blob = new Blob([bytes], { type: labelFile.mime || 'application/pdf' })
			return URL.createObjectURL(blob)
		} catch {
			return ''
		}
	}, [labelFile])

	React.useEffect(
		() => () => {
			if (previewUrl !== '') URL.revokeObjectURL(previewUrl)
		},
		[previewUrl],
	)

	const onClose = React.useCallback(() => {
		reduxDispatch(routeActions.NAVIGATE_PATH(detailPath(dispatchId)))
	}, [dispatchId, reduxDispatch])

	const onPrint = () => {
		if (labelFile === null) return
		printLabel({
			base64: labelFile.base64,
			mime: labelFile.mime,
			fileName: `label-${labelFile.trackingNumber}`,
		})
	}

	const onDownload = () => {
		if (previewUrl === '' || labelFile === null) return
		const a = document.createElement('a')
		a.href = previewUrl
		a.download = `label-${labelFile.trackingNumber || dispatchId}.pdf`
		document.body.appendChild(a)
		a.click()
		a.remove()
	}

	if (dispatchId === '') return null

	/** 머리에 쓰는 이름 — 주문 건은 주문번호, 수동 건은 거래처/고객 */
	const title: string =
		detail?.dispatch.orderId !== undefined && detail.dispatch.orderId !== ''
			? detail.dispatch.orderId
			: detail?.dispatch.partyName ?? dispatchId

	const loading = labelFileStatus === 'loading' || labelFileStatus === 'init'

	return (
		<FullModalLayout
			overlay
			size="large"
			title={title}
			onCloseClick={onClose}
			customTitle={
				<View className="labelHead" css={headStyle}>
					<span className="boKicker">라벨</span>
					<View className="labelHeadRow">
						<h1 className="labelNumber">
							{target?.trackingNumber !== undefined && target.trackingNumber !== ''
								? target.trackingNumber
								: title}
						</h1>
						{target?.canceledAt !== undefined && target.canceledAt !== '' && (
							<span className="boChip faint">취소된 건</span>
						)}
					</View>
					<span className="labelHeadMeta">
						{[
							title,
							target?.serviceType ?? '',
							target !== undefined ? `발급 ${datetime(target.createdAt)}` : '',
						]
							.filter(aPart => aPart !== '')
							.join(' · ')}
					</span>
				</View>
			}
		>
			<View css={style}>
				{target === undefined ? (
					<View className="boSection">
						<Text className="boEmpty">이 배송 건에는 저장된 라벨이 없습니다.</Text>
						<button type="button" className="boBtnGhost" onClick={onClose}>
							배송 자세히로
						</button>
					</View>
				) : (
					<>
						<View className="actionBar">
							<View className="metaRow">
								<span className="metaItem">
									송장{' '}
									<a
										href={`https://www.fedex.com/fedextrack/?trknbr=${target.trackingNumber}`}
										target="_blank"
										rel="noreferrer"
									>
										{target.trackingNumber || EMPTY}
									</a>
								</span>
								<span className="metaItem">
									{[
										target.boxCode,
										`${target.widthCm}×${target.heightCm}×${target.depthCm} cm`,
										`${target.weightKg} kg`,
									]
										.filter(aPart => String(aPart ?? '') !== '')
										.join(' · ')}
								</span>
								{labelFile !== null && (
									<span className="metaItem faint">
										{labelFile.s3Key !== '' ? 'S3 보관' : 'DB 보관(옛 방식)'}
									</span>
								)}
							</View>
							<View className="actionRight">
								<button
									type="button"
									className="boBtnGhost"
									onClick={onDownload}
									disabled={labelFile === null}
								>
									PDF 내려받기
								</button>
								<button
									type="button"
									className="boBtn"
									onClick={onPrint}
									disabled={labelFile === null}
								>
									인쇄
								</button>
							</View>
						</View>

						{loading && <Text className="boEmpty">라벨을 읽는 중…</Text>}
						{!loading && previewUrl === '' && (
							<Text className="boEmpty">
								라벨을 읽지 못했습니다. 다시 시도하거나 라벨을 새로 만듭니다.
							</Text>
						)}
						{previewUrl !== '' && (
							<iframe className="labelFrame" title="라벨 미리보기" src={previewUrl} />
						)}
					</>
				)}
			</View>
		</FullModalLayout>
	)
}

const headStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: 8,
	fontFamily: boFont.ui,

	'.boKicker': {
		fontSize: 11,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},
	'.labelHeadRow': { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
	'.labelNumber': {
		margin: 0,
		fontSize: 'clamp(24px, 2.8vw, 34px)',
		fontWeight: 200,
		lineHeight: 1.1,
		color: mono.ink,
		fontVariantNumeric: 'tabular-nums',
	},
	'.boChip': boShared['.boChip'],
	'.labelHeadMeta': { fontSize: 13, fontWeight: 300, color: mono.dim },
})

const style = css({
	...boShared,
	paddingTop: 8,

	'.actionBar': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		paddingBottom: 16,
		borderBottom: `1px solid ${mono.line}`,
		'.metaRow': { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
		'.metaItem': { fontSize: 13, fontWeight: 300, color: mono.ink },
		'.faint': { color: mono.faint },
		'.actionRight': { display: 'flex', alignItems: 'center', gap: 8 },
	},

	'.labelFrame': {
		width: '100%',
		height: 'min(72vh, 900px)',
		marginTop: 16,
		border: `1px solid ${mono.line}`,
		background: '#FFFFFF',
	},

	a: { color: mono.ink },
})
